import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Globe, Plus, Loader2, Trash2, RefreshCw, ExternalLink, 
  CheckCircle2, XCircle, FileText, FolderOpen, BarChart3,
  Eye, Calendar
} from "lucide-react";

export default function SitesPage() {
  const { token } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [siteDetails, setSiteDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [selectedSite, setSelectedSite] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    username: "",
    app_password: ""
  });

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API}/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      }
    } catch (error) {
      console.error("Failed to fetch sites:", error);
      toast.error("Erreur lors du chargement des sites");
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteDetails = async (siteId) => {
    setLoadingDetails(prev => ({ ...prev, [siteId]: true }));
    
    try {
      const [statsRes, catsRes, postsRes] = await Promise.all([
        fetch(`${API}/sites/${siteId}/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/sites/${siteId}/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/sites/${siteId}/posts?per_page=5`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const stats = statsRes.ok ? await statsRes.json() : {};
      const categories = catsRes.ok ? await catsRes.json() : [];
      const posts = postsRes.ok ? await postsRes.json() : [];

      setSiteDetails(prev => ({
        ...prev,
        [siteId]: { stats, categories, posts }
      }));
    } catch (error) {
      console.error("Failed to fetch site details:", error);
      toast.error("Erreur lors du chargement des détails");
    } finally {
      setLoadingDetails(prev => ({ ...prev, [siteId]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await fetch(`${API}/sites`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success("Site ajouté avec succès");
        setDialogOpen(false);
        setFormData({ name: "", url: "", username: "", app_password: "" });
        fetchSites();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'ajout du site");
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout du site");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (siteId) => {
    try {
      const response = await fetch(`${API}/sites/${siteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Site supprimé");
        if (selectedSite?.id === siteId) setSelectedSite(null);
        fetchSites();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const testConnection = async (siteId) => {
    setTesting((prev) => ({ ...prev, [siteId]: true }));
    
    try {
      const response = await fetch(`${API}/sites/${siteId}/test`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = await response.json();
      
      if (result.status === "connected") {
        toast.success(result.message);
        setSites((prev) =>
          prev.map((site) =>
            site.id === siteId ? { ...site, connectionStatus: "connected" } : site
          )
        );
        fetchSiteDetails(siteId);
      } else {
        toast.error(`Échec: ${result.message}`);
        setSites((prev) =>
          prev.map((site) =>
            site.id === siteId ? { ...site, connectionStatus: "error" } : site
          )
        );
      }
    } catch (error) {
      toast.error("Erreur de test de connexion");
    } finally {
      setTesting((prev) => ({ ...prev, [siteId]: false }));
    }
  };

  const openSiteDetails = (site) => {
    setSelectedSite(site);
    if (!siteDetails[site.id]) {
      fetchSiteDetails(site.id);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="sites-loading">
        <div className="animate-pulse text-[#6B7280]">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="sites-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1E2A5A]">Sites WordPress</h1>
          <p className="text-[#6B7280] mt-1">Gérez vos sites WordPress connectés</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E2A5A] hover:bg-[#20C997] rounded-xl transition-colors" data-testid="add-site-dialog-btn">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un site
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-[#1E2A5A]">Ajouter un site WordPress</DialogTitle>
              <DialogDescription className="text-[#6B7280]">
                Connectez un site WordPress via l'API REST avec Application Password
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#1E2A5A]">Nom du site</Label>
                <Input
                  id="name"
                  placeholder="Mon Blog"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="site-name-input"
                  className="rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="url" className="text-[#1E2A5A]">URL du site</Label>
                <Input
                  id="url"
                  placeholder="monsite.com"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  required
                  data-testid="site-url-input"
                  className="rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997]"
                />
                <p className="text-xs text-[#6B7280]">https:// sera ajouté automatiquement</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="username" className="text-[#1E2A5A]">Identifiant WordPress</Label>
                <Input
                  id="username"
                  placeholder="admin"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  data-testid="site-username-input"
                  className="rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997]"
                />
                <p className="text-xs text-[#6B7280]">Votre login WordPress (pas l'email)</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="app_password" className="text-[#1E2A5A]">Application Password</Label>
                <Input
                  id="app_password"
                  type="password"
                  placeholder="xxxx xxxx xxxx xxxx"
                  value={formData.app_password}
                  onChange={(e) => setFormData({ ...formData, app_password: e.target.value })}
                  required
                  data-testid="site-password-input"
                  className="rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997]"
                />
                <p className="text-xs text-[#6B7280]">
                  WordPress → Utilisateurs → Profil → Mots de passe d'application
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#1E2A5A] hover:bg-[#20C997] rounded-xl transition-colors" 
                  disabled={saving}
                  data-testid="site-submit-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sites List */}
        <div className="lg:col-span-1 space-y-4">
          {sites.length > 0 ? (
            sites.map((site) => (
              <Card 
                key={site.id} 
                className={`border-0 bg-white rounded-2xl soft-shadow cursor-pointer transition-all duration-200 hover-lift ${
                  selectedSite?.id === site.id 
                    ? 'ring-2 ring-[#20C997]' 
                    : ''
                }`}
                onClick={() => openSiteDetails(site)}
                data-testid={`site-card-${site.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#1E2A5A]/10 rounded-xl flex items-center justify-center">
                        <Globe className="w-6 h-6 text-[#1E2A5A]" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1E2A5A]">{site.name}</h3>
                        <p className="text-xs text-[#6B7280] truncate max-w-[150px]">
                          {site.url}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {site.connectionStatus === "connected" && (
                        <CheckCircle2 className="w-5 h-5 text-[#20C997]" />
                      )}
                      {site.connectionStatus === "error" && (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 rounded-xl border-gray-200 hover:bg-[#F5F7FA] text-[#1E2A5A]"
                      onClick={(e) => { e.stopPropagation(); testConnection(site.id); }}
                      disabled={testing[site.id]}
                      data-testid={`test-site-${site.id}`}
                    >
                      {testing[site.id] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Tester
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="rounded-xl border-gray-200 hover:bg-[#F5F7FA] text-[#1E2A5A]"
                      onClick={(e) => { e.stopPropagation(); window.open(`${site.url}/wp-admin`, "_blank"); }}
                      data-testid={`admin-site-${site.id}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="border-0 bg-white rounded-2xl soft-shadow">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 bg-[#F5F7FA] rounded-full flex items-center justify-center mb-4">
                  <Globe className="w-8 h-8 text-[#6B7280]" />
                </div>
                <p className="text-[#6B7280] text-center mb-4">Aucun site connecté</p>
                <Button 
                  className="bg-[#1E2A5A] hover:bg-[#20C997] rounded-xl" 
                  onClick={() => setDialogOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un site
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Site Details Panel */}
        <div className="lg:col-span-2">
          {selectedSite ? (
            <Card className="border-0 bg-white rounded-2xl soft-shadow">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-xl text-[#1E2A5A]">{selectedSite.name}</CardTitle>
                    <CardDescription className="text-[#6B7280]">{selectedSite.url}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="rounded-xl border-gray-200 text-[#1E2A5A] hover:bg-[#F5F7FA]"
                      onClick={() => window.open(`${selectedSite.url}/wp-admin`, "_blank")}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Admin
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50 border-gray-200">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-2xl">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-[#1E2A5A]">Supprimer ce site ?</AlertDialogTitle>
                          <AlertDialogDescription className="text-[#6B7280]">
                            Le site sera déconnecté de Diffuzo.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDelete(selectedSite.id)}
                            className="bg-red-500 hover:bg-red-600 rounded-xl"
                          >
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingDetails[selectedSite.id] ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#20C997]" />
                  </div>
                ) : siteDetails[selectedSite.id] ? (
                  <Tabs defaultValue="stats" className="w-full">
                    <TabsList className="grid w-full grid-cols-3 rounded-xl bg-[#F5F7FA]">
                      <TabsTrigger value="stats" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#1E2A5A]">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Stats
                      </TabsTrigger>
                      <TabsTrigger value="categories" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#1E2A5A]">
                        <FolderOpen className="w-4 h-4 mr-2" />
                        Catégories
                      </TabsTrigger>
                      <TabsTrigger value="posts" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#1E2A5A]">
                        <FileText className="w-4 h-4 mr-2" />
                        Articles
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="stats" className="mt-6">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-[#F5F7FA] rounded-xl p-4 text-center">
                          <p className="text-3xl font-bold text-[#1E2A5A]">
                            {siteDetails[selectedSite.id].stats.posts || 0}
                          </p>
                          <p className="text-sm text-[#6B7280]">Articles</p>
                        </div>
                        <div className="bg-[#F5F7FA] rounded-xl p-4 text-center">
                          <p className="text-3xl font-bold text-[#20C997]">
                            {siteDetails[selectedSite.id].stats.pages || 0}
                          </p>
                          <p className="text-sm text-[#6B7280]">Pages</p>
                        </div>
                        <div className="bg-[#F5F7FA] rounded-xl p-4 text-center">
                          <p className="text-3xl font-bold text-[#1FB6FF]">
                            {siteDetails[selectedSite.id].stats.categories || 0}
                          </p>
                          <p className="text-sm text-[#6B7280]">Catégories</p>
                        </div>
                        <div className="bg-[#F5F7FA] rounded-xl p-4 text-center">
                          <p className="text-3xl font-bold text-amber-500">
                            {siteDetails[selectedSite.id].categories?.length || 0}
                          </p>
                          <p className="text-sm text-[#6B7280]">Tags</p>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="categories" className="mt-6">
                      <div className="space-y-2">
                        {siteDetails[selectedSite.id].categories?.length > 0 ? (
                          siteDetails[selectedSite.id].categories.map((cat) => (
                            <div 
                              key={cat.id}
                              className="flex items-center justify-between p-3 bg-[#F5F7FA] rounded-xl"
                            >
                              <div className="flex items-center gap-3">
                                <FolderOpen className="w-4 h-4 text-[#6B7280]" />
                                <span className="font-medium text-[#1E2A5A]">{cat.name}</span>
                              </div>
                              <Badge className="rounded-lg bg-[#1E2A5A]/10 text-[#1E2A5A] hover:bg-[#1E2A5A]/10">{cat.count} articles</Badge>
                            </div>
                          ))
                        ) : (
                          <p className="text-center text-[#6B7280] py-8">
                            Aucune catégorie trouvée
                          </p>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="posts" className="mt-6">
                      <div className="space-y-3">
                        {siteDetails[selectedSite.id].posts?.length > 0 ? (
                          siteDetails[selectedSite.id].posts.map((post) => (
                            <div 
                              key={post.id}
                              className="p-4 bg-[#F5F7FA] rounded-xl hover:bg-[#1E2A5A]/5 transition-colors"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium text-[#1E2A5A]" dangerouslySetInnerHTML={{ __html: post.title }} />
                                  <div className="flex items-center gap-3 mt-2 text-xs text-[#6B7280]">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {new Date(post.date).toLocaleDateString('fr-FR')}
                                    </span>
                                    <Badge className="text-xs rounded-lg bg-[#20C997]/10 text-[#20C997] hover:bg-[#20C997]/10">
                                      {post.status === 'publish' ? 'Publié' : post.status}
                                    </Badge>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-[#6B7280] hover:text-[#1E2A5A]"
                                  onClick={() => window.open(post.link, "_blank")}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-center text-[#6B7280] py-8">
                            Aucun article trouvé
                          </p>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-[#6B7280] mb-4">Testez la connexion pour voir les détails</p>
                    <Button onClick={() => testConnection(selectedSite.id)} className="bg-[#1E2A5A] hover:bg-[#20C997] rounded-xl">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Tester la connexion
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 bg-white rounded-2xl soft-shadow h-full min-h-[400px]">
              <CardContent className="flex flex-col items-center justify-center h-full py-16">
                <div className="w-20 h-20 bg-[#F5F7FA] rounded-full flex items-center justify-center mb-4">
                  <Globe className="w-10 h-10 text-[#6B7280]/50" />
                </div>
                <p className="text-[#6B7280]">Sélectionnez un site pour voir les détails</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
